import React, { Suspense } from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

import "us.style/antd.less";
import "us.style/index.scss";
import "us.helper/utility/dayjsConfig";
import { Loader } from "us.common/components/Loader";

import App from "App";
import * as serviceWorker from "serviceWorker";
import store from "store";
import * as Azure from "us.helper/azure";

import {
  PublicClientApplication,
  EventType,
  AuthenticationResult,
  EventMessage,
} from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { setupTitle } from "us.helper";

// Set global identifier
window.APP_MODULE = "io";

// Configure pdf.js worker globally
pdfjsLib.GlobalWorkerOptions.workerSrc = "/workers/pdf.worker.min.js";
// Initialize MSAL instance
export const msalInstance = new PublicClientApplication(Azure.msalConfig);

// Set active account on page load if available
const accounts = msalInstance.getAllAccounts();
if (accounts.length > 0 && !msalInstance.getActiveAccount()) {
  msalInstance.setActiveAccount(accounts[0]);
}

// Sync account state across tabs
msalInstance.enableAccountStorageEvents();

// Handle MSAL authentication events
msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
    const account = (event.payload as AuthenticationResult).account;
    msalInstance.setActiveAccount(account);
  } else if (event.eventType === EventType.LOGOUT_SUCCESS) {
    msalInstance.setActiveAccount(null);
    window.location.reload();
  } else if (event.eventType === EventType.ACCOUNT_ADDED && event.payload) {
    const account = (event.payload as AuthenticationResult).account;
    msalInstance.setActiveAccount(account);
  } else if (event.eventType === EventType.ACCOUNT_REMOVED) {
    msalInstance.setActiveAccount(null);
    window.location.reload();
  }
});

// Setup dynamic page title
setupTitle();

// React application with Redux and MSAL providers
const app = (
  <Provider store={store}>
    <MsalProvider instance={msalInstance}>
      <Suspense fallback={<Loader />}>
        <App />
      </Suspense>
    </MsalProvider>
  </Provider>
);

// Mount React app
const container = document.getElementById("root");
if (container) {
  const root = createRoot(container);
  root.render(app);
}

// Optional: PWA support (disabled by default)
serviceWorker.unregister();
